import React, { useState, useEffect } from 'react';
import { addDoc, collection, doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
// Navigation uses window.location.href for reliability
import { calculateAge, getAgeString } from '../utils/dateHelpers';
import { auth, db, storage } from '../firebaseConfig';
import InfoTip from './InfoTip';


const sortDates = (dates) =>
  dates.slice().sort((a, b) => new Date(a) - new Date(b));

const PostJob = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
const [selectedDates, setSelectedDates] = useState([]);
  const [payRange, setPayRange] = useState(''); 
  const [profilePictureURL, setProfilePictureURL] = useState(''); 
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loadingPaymentMethod, setLoadingPaymentMethod] = useState(true);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [dateTimeRanges, setDateTimeRanges] = useState({});
  const [schedule, setSchedule] = useState({});
  const [savedChildren, setSavedChildren] = useState([]); // Children saved in parent's profile
  const [selectedChildren, setSelectedChildren] = useState([]); // Children selected for this job
  const [additionalChildren, setAdditionalChildren] = useState([]); // New children added for this job only

  // Navigation uses window.location.href for reliability
  const user = auth.currentUser;

  // Add CSS to fix dropdown z-index issues and mobile input overflow
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .post-job-select {
        position: relative;
        z-index: 1000;
      }
      .post-job-select:focus {
        z-index: 1001;
      }
      .post-job-select option {
        background: white;
        color: #374151;
      }
      .child-age-container {
        position: relative;
        z-index: 1;
        margin-bottom: 1rem;
      }
      .child-age-container:last-child {
        margin-bottom: 0;
      }
      .child-age-container .flex {
        gap: 0.75rem;
      }
      .child-age-container input,
      .child-age-container select {
        box-sizing: border-box;
      }
      .child-age-container select {
        min-width: 80px;
        width: auto;
      }
      /* Fix mobile Safari input overflow */
      input[type="date"],
      input[type="time"] {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        max-width: 100%;
        box-sizing: border-box;
        min-width: 0;
      }
      @media (max-width: 640px) {
        input[type="date"],
        input[type="time"] {
          font-size: 16px; /* Prevent iOS zoom on focus */
          padding-left: 8px;
          padding-right: 8px;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        setLoadingPaymentMethod(true);
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setProfilePictureURL(userData.profilePictureURL);
          if (userData.paymentMethodId) {
            // User has a payment method
            setPaymentMethod(userData.paymentMethodId);
          }
          // Load saved children from profile
          if (userData.children && Array.isArray(userData.children)) {
            setSavedChildren(userData.children.filter(child => child.dateOfBirth)); // Only include children with DOB
          }
        }
        setLoadingPaymentMethod(false);
      }
    };

    fetchUserProfile();
  }, [auth, db, user]);

  useEffect(() => {
    if (!startDate || !endDate) return;
  
    const validDates = generateDateRange(startDate, endDate);
    setDateTimeRanges((prev) => {
      const updated = {};
      validDates.forEach((date) => {
        updated[date] = Array.isArray(prev[date])
          ? prev[date]
          : prev[date]
          ? [prev[date]]
          : [{ start: '', end: '' }];
      });
      return updated;
    });
    
    // Auto-select all dates initially
    setSelectedDates(sortDates(validDates));
  }, [startDate, endDate]);

  const generateDateRange = (start, end) => {
    // Parse dates by splitting the YYYY-MM-DD string to avoid timezone issues
    const [startYear, startMonth, startDay] = start.split('-').map(Number);
    const [endYear, endMonth, endDay] = end.split('-').map(Number);
    
    // Create date objects using local timezone constructor
    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);
    const dates = [];
  
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      // Format date as YYYY-MM-DD without timezone conversion
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }
  
    return dates;
  };

  const handlePost = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        alert('User not authenticated');
        setLoading(false);
        window.location.href = '/auth';
        return;
      }

      // Validate required fields
      if (!title.trim()) {
        alert('Job title is required.');
        setLoading(false);
        return;
      }

      if (!description.trim()) {
        alert('Job description is required.');
        setLoading(false);
        return;
      }

      if (!startDate) {
        alert('Start date is required.');
        setLoading(false);
        return;
      }

      // If no end date specified, treat it as a single-day job
      const effectiveEndDate = endDate || startDate;
      if (new Date(effectiveEndDate) < new Date(startDate)) {
        alert('End date cannot be before start date.');
        setLoading(false);
        return;
      }

      // Pay per hour validation
      if (!payRange || isNaN(payRange) || payRange < 22) {
        alert('Minimum pay rate is $22/hr.');
        setLoading(false);
        return;
      }

      if (!/^\d+(\.\d{1,2})?$/.test(payRange)) {
        alert('Please enter a valid pay rate with no more than 2 decimal places.');
        setLoading(false);
        return;
      }

      // Start date validation - allow same day jobs
      const today = new Date();
      const todayYear = today.getFullYear();
      const todayMonth = today.getMonth();
      const todayDay = today.getDate();
      const todayStart = new Date(todayYear, todayMonth, todayDay);
      
      const selectedStartDate = new Date(startDate + 'T00:00:00');
      if (selectedStartDate < todayStart) {
        alert('Start date must be today or later.');
        setLoading(false);
        return;
      }

      // Note: Same day jobs are allowed - no 24-hour advance notice required

      // Validate date range for selection purposes
      if (endDate && endDate !== startDate) {
        const selectedEndDate = new Date(endDate + 'T00:00:00');
        if (selectedEndDate < selectedStartDate) {
          alert('End date must be on or after the start date to define the selection range.');
          setLoading(false);
          return;
        }
      }

      // Validate that dates are selected
      if (selectedDates.length === 0) {
        alert('Please select at least one date for the job.');
        setLoading(false);
        return;
      }

      // Check maximum job length (1 week = 7 days)
      if (selectedDates.length > 7) {
        alert('Job cannot exceed one week (7 days). Please select fewer dates.');
        setLoading(false);
        return;
      }

      // Validate children - must have at least one child selected or added
      const totalChildren = selectedChildren.length + additionalChildren.length;
      if (totalChildren === 0) {
        alert('Please select at least one child from your profile or add a new child.');
        setLoading(false);
        return;
      }

      // Validate that all additional children have date of birth
      for (let i = 0; i < additionalChildren.length; i++) {
        const child = additionalChildren[i];
        if (!child.dateOfBirth || child.dateOfBirth.trim() === '') {
          alert(`Please enter the date of birth for additional child ${i + 1}.`);
          setLoading(false);
          return;
        }
      }

      // Combine selected and additional children with calculated ages
      const allJobChildren = [
        ...selectedChildren.map(childId => {
          const child = savedChildren.find(c => c.id === childId);
          if (!child || !child.dateOfBirth) {
            throw new Error(`Selected child with ID ${childId} not found or missing date of birth`);
          }
          return {
            ...child,
            age: calculateAge(child.dateOfBirth)
          };
        }).filter(Boolean), // Remove any undefined entries
        ...additionalChildren.map(child => ({
          ...child,
          age: calculateAge(child.dateOfBirth)
        }))
      ];

      // Calculate total duration
      const dateKeys = selectedDates;
      let totalMinutes = 0;

      for (let date of dateKeys) {
        const timeRanges = Array.isArray(dateTimeRanges[date])
          ? dateTimeRanges[date]
          : dateTimeRanges[date]
            ? [dateTimeRanges[date]]
            : [];

        if (timeRanges.length === 0) {
          alert(`Please enter at least one time range for ${date}`);
          setLoading(false);
          return;
        }

        let dayMinutes = 0;

        for (let i = 0; i < timeRanges.length; i++) {
          const { start, end } = timeRanges[i];

          if (!start || !end) {
            alert(`Please enter both start and end times for range ${i + 1} on ${date}`);
            setLoading(false);
            return;
          }

          const [startH, startM] = start.split(":").map(Number);
          const [endH, endM] = end.split(":").map(Number);

          const startTime = startH * 60 + startM;
          const endTime = endH * 60 + endM;

          if (startTime >= endTime) {
            alert(`Start time must be before end time for range ${i + 1} on ${date}.\n\nFor overnight care, split into two date entries: end the first day at 11:59 PM and start the next day at 12:00 AM.`);
            setLoading(false);
            return;
          }

          const rangeMinutes = endTime - startTime;
          if (rangeMinutes < 180) {
            const hrs = Math.floor(rangeMinutes / 60);
            const mins = rangeMinutes % 60;
            alert(`There is a 3-hour minimum required for all time ranges. Range ${i + 1} on ${date} is only ${hrs}h ${mins}m. Please adjust your times.`);
            setLoading(false);
            return;
          }
          dayMinutes += rangeMinutes;
        }

        totalMinutes += dayMinutes;
      }
  
      // Fetch the user's profile to get the address
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        alert('User data not found.');
        setLoading(false);
        return;
      }
  
      const userProfile = userDoc.data();
      const parentName = userProfile.name || 'Anonymous';
      const userAddress = userProfile.address;
  
      if (!userAddress) {
        alert('Please update your profile to include an address before posting a job.');
        setLoading(false);
        return;
      }
  
      // Geocode the address to get latitude and longitude
      let location = null;
      try {
        const geocodeResponse = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            userAddress
          )}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDCCNLE719tfQRXV3rqPZBZDBOXR-UGy9E'}`
        );
        const geocodeData = await geocodeResponse.json();
        if (geocodeData.status === 'OK' && geocodeData.results.length > 0) {
          const { lat, lng } = geocodeData.results[0].geometry.location;
          location = { latitude: lat, longitude: lng };
        } else {
          alert('Unable to fetch location from address. Please check your profile address.');
          setLoading(false);
          return;
        }
      } catch (geocodeError) {
        alert('Error fetching location: ' + geocodeError.message);
        setLoading(false);
        return;
      }
  
      // profilePictureURL is the parent's existing profile pic URL loaded from
      // their user doc — it's already a stored Storage URL, so just pass it through.
      // (The old code tried to re-upload the string as a File, which silently no-op'd
      // or threw depending on the runtime.)
      const profilePictureURL1 = profilePictureURL || '';

      const sortedSchedule = selectedDates
        .slice()
        .sort((a, b) => new Date(a) - new Date(b))
        .reduce((acc, date) => {
          const ranges = Array.isArray(dateTimeRanges[date]) ? dateTimeRanges[date] : [];
          const validRanges = ranges.filter(
            (range) => range && range.start && range.end
          );
          if (validRanges.length > 0) {
            acc[date] = validRanges;
          }
          return acc;
        }, {});

      setSchedule(sortedSchedule);

      const sortedScheduleDates = Object.keys(sortedSchedule);
      if (sortedScheduleDates.length === 0) {
        alert('Please add time ranges for at least one date.');
        setLoading(false);
        return;
      }
      const derivedStartTime = sortedSchedule[sortedScheduleDates[0]][0].start;
      
      
      await addDoc(collection(db, 'jobs'), {
        title,
        description,
        startDate,
        jobStartTime: derivedStartTime,
        endDate: effectiveEndDate,
        numChildren: totalChildren,
        children: allJobChildren, // Store children with DOB and calculated ages
        payRange,
        // Use sortedSchedule directly — the `schedule` state variable is stale here
        // because setSchedule(sortedSchedule) at line 401 is async and doesn't
        // update the closure value we'd be reading below.
        schedule: sortedSchedule,
        location, // Save latitude and longitude
        address: userAddress, // Save the address for display purposes
        profilePictureURL: profilePictureURL1,
        createdAt: new Date(),
        parentId: user.uid,
        parentName,
        parentEmail: user.email,
        status: 'Open',
        zipCode: userProfile.zipCode,
        city: userProfile.city,
        state: userProfile.state,

      });
  
      alert('Job posted successfully!');
      window.location.href = '/parent-jobs';
    } catch (error) {
      alert('Error posting job: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-3 sm:px-4 overflow-x-hidden">
      <div className="max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Post a Job</h1>
          <p className="text-gray-600 text-lg">Create a new job posting for sitters</p>
        </div>

        {/* Payment Method Warning */}
      {!paymentMethod && !loadingPaymentMethod && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="font-medium">Payment Method Required</span>
            </div>
            <p className="mt-2 text-sm">You must add a payment method to your profile before posting a job.</p>
            <button 
              onClick={() => window.location.href = '/profile/' + user.uid} 
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200"
            >
            Add Payment Method
          </button>
        </div>
      )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Job Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Job Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                Job Details
              </h2>
              
              <div className="space-y-4">
      <div>
                  <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
          Job Title
        </label>
        <input
          type="text"
                    id="title"
                    placeholder="e.g., Weekend Babysitter Needed"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
        />
      </div>

      <div>
                  <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
          Job Description
        </label>
        <textarea
                    id="description"
                    placeholder="Describe the job responsibilities, requirements, and any special needs..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white resize-none"
                    rows="4"
        />
      </div>
              </div>
            </div>

            {/* Schedule Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                Schedule & Dates
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Job Dates <InfoTip text="Every day between your start and end date gets its own time slots (3-hour minimum each). For overnight care, end day one at 11:59 PM and start day two at 12:00 AM." />
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="min-w-0">
                      <label htmlFor="startDate" className="block text-xs text-gray-600 mb-1">
                        Start Date (Earliest)
                      </label>
                      <input
                        type="date"
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full max-w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white text-sm box-border"
                      />
                    </div>

                    <div className="min-w-0">
                      <label htmlFor="endDate" className="block text-xs text-gray-600 mb-1">
                        End Date (Latest)
                      </label>
                      <input
                        type="date"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full max-w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white text-sm box-border"
                      />
                    </div>
                  </div>

      {startDate && endDate && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">Available Dates:</span>
                        <button
                          type="button"
                          onClick={() => {
                            const dates = generateDateRange(startDate, endDate);
                            setSelectedDates(sortDates(dates));
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                        >
                          Select All Dates
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-7 gap-2 max-h-32 overflow-y-auto p-3 bg-gray-50 rounded-lg border">
                        {generateDateRange(startDate, endDate).map((date) => {
                          const isSelected = selectedDates.includes(date);
                          const isToday = date === new Date().toISOString().split('T')[0];
                          const isPast = date < new Date().toISOString().split('T')[0];
                          
                          return (
                            <button
                              key={date}
                              type="button"
                              onClick={() => {
                                if (isPast) return; // Prevent selecting past dates
                                setSelectedDates((prev) => {
                                  const next = isSelected
                                    ? prev.filter((d) => d !== date)
                                    : [...prev, date];
                                  return sortDates(next);
                                });
                              }}
                              disabled={isPast}
                              className={`
                                p-2 text-xs rounded-lg border transition-all duration-200
                                ${isSelected 
                                  ? 'bg-blue-500 text-white border-blue-500' 
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                }
                                ${isToday ? 'ring-2 ring-blue-300' : ''}
                                ${isPast ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'cursor-pointer'}
                              `}
                            >
                              {date.split('-')[2]}
                            </button>
                          );
                        })}
                      </div>
                      
                      <div className="mt-2 text-xs text-gray-600">
                        Selected: {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''}
                        {selectedDates.length > 7 && (
                          <span className="text-orange-600 font-medium ml-2">
                            ⚠️ Maximum 7 days allowed
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selectedDates.length > 0 && (
                <div className="mt-4">
        <button
                    className="w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 hover:border-blue-400 hover:text-blue-700 transition-all duration-200 hover:bg-blue-50"
          onClick={() => setShowTimeModal(true)}
        >
                    <svg className="w-5 h-5 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Set Time for {selectedDates.length} Selected Day{selectedDates.length !== 1 ? 's' : ''}
        </button>
                </div>
              )}
            </div>

            {/* Family Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                Family Information
              </h2>
              
              <div className="space-y-4">
        {/* Children Selection */}
        <div className="space-y-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Children for this Job
          </label>
          
          {/* Saved Children from Profile */}
          {savedChildren.length > 0 && (
            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
              <p className="text-sm font-medium text-gray-700 mb-3">Select from your profile:</p>
              <div className="space-y-2">
                {savedChildren.map((child) => (
                  <label key={child.id} className="flex items-center space-x-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedChildren.includes(child.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedChildren([...selectedChildren, child.id]);
                        } else {
                          setSelectedChildren(selectedChildren.filter(id => id !== child.id));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">
                        {child.name || 'Child'}
                      </span>
                      <span className="text-sm text-gray-600 ml-2">
                        ({getAgeString(child.dateOfBirth)})
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Add Additional Children */}
          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-700">Additional children (not in profile):</p>
              <button
                type="button"
                onClick={() => {
                  setAdditionalChildren([
                    ...additionalChildren,
                    { id: `temp-${Date.now()}`, name: '', dateOfBirth: '' }
                  ]);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Child
              </button>
            </div>
            
            {additionalChildren.length > 0 && (
              <div className="space-y-3">
                {additionalChildren.map((child, index) => (
                  <div key={child.id} className="border border-gray-300 rounded-lg p-3 bg-white">
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Child {selectedChildren.length + index + 1}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setAdditionalChildren(additionalChildren.filter((_, i) => i !== index));
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={child.name}
                        onChange={(e) => {
                          const updated = [...additionalChildren];
                          updated[index] = { ...updated[index], name: e.target.value };
                          setAdditionalChildren(updated);
                        }}
                        placeholder="Name (optional)"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                      <div>
                        <input
                          type="date"
                          value={child.dateOfBirth}
                          onChange={(e) => {
                            const updated = [...additionalChildren];
                            updated[index] = { ...updated[index], dateOfBirth: e.target.value };
                            setAdditionalChildren(updated);
                          }}
                          max={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        {child.dateOfBirth && (
                          <p className="text-xs text-gray-500 mt-1">
                            Age: {getAgeString(child.dateOfBirth)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {additionalChildren.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-2">
                No additional children added
              </p>
            )}
          </div>

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-900">
              Total children for this job: {selectedChildren.length + additionalChildren.length}
            </p>
          </div>
        </div>
              </div>
            </div>
      </div>

          {/* Right Column - Payment & Actions */}
          <div className="space-y-6">
            {/* Payment Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                Payment Details
              </h2>
              
      <div>
                <label htmlFor="payRange" className="block text-sm font-semibold text-gray-700 mb-2">
          Pay Rate Per Hour ($) <InfoTip align="right" text="Minimum $22/hr. Sitters receive this full rate — the 10% platform fee and card processing are added to your total at checkout." />
        </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg font-medium">
                    $
                  </span>
        <input
          id="payRange"
          type="number"
          value={payRange}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*\.?\d{0,2}$/.test(value)) {
              setPayRange(value);
            }
          }}
          onBlur={(e) => {
            // Format to 2 decimal places when user leaves the field
            const value = e.target.value;
            if (value && !isNaN(parseFloat(value))) {
              setPayRange(parseFloat(value).toFixed(2));
            }
          }}
                    step="0.01"
                    min="22"
                    placeholder="22.00"
                    className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white text-lg"
        />
      </div>
                <p className="text-xs text-gray-500 mt-2">Enter amount with up to 2 decimal places</p>
              </div>
            </div>

            {/* Action Buttons Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                Post Job
              </h2>
              
              <button
                onClick={handlePost}
                disabled={!paymentMethod || loading}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Posting Job...
                  </div>
                ) : (
                  'Post Job'
                )}
              </button>
              
              {!paymentMethod && (
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Payment method required to post jobs
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Time Modal */}
        {showTimeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto my-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Set Time for Each Day</h2>
        <button
                  onClick={() => setShowTimeModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
        >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
        </button>
      </div>

              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                There is a 3-hour minimum required for all time ranges.
              </p>

              <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4">
                <strong>Overnight care?</strong> Split into two date entries — end the first day at 11:59 PM and start the next day at 12:00 AM. (e.g., for Fri 9 PM – Sat 5 AM: enter Fri 9:00 PM – 11:59 PM and Sat 12:00 AM – 5:00 AM.)
              </p>

              <div className="mb-6">
            <button
                  className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-green-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
              onClick={() => {
                    if (!selectedDates.length) return;

                    const first = dateTimeRanges[selectedDates[0]];
                if (!Array.isArray(first) || first.length === 0 || !first[0].start || !first[0].end) {
                  alert("Please fill in the first date's start and end time before applying to all.");
                  return;
                }

                const newRanges = {};
                    selectedDates.forEach(date => {
                      newRanges[date] = first.map(range => ({ ...range }));
                });

                setDateTimeRanges(newRanges);
              }}
            >
                  <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
              Apply First Day's Time to All
            </button>
              </div>

              <div className="space-y-6">
                {selectedDates.map((date) => (
                  <div key={date} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <label className="block font-semibold mb-3 text-gray-700 text-lg">
                      {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </label>

                {(Array.isArray(dateTimeRanges[date]) ? dateTimeRanges[date] : [dateTimeRanges[date] || { start: '', end: '' }]).map((range, index) => (
                      <div key={index} className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                        <div className="grid grid-cols-2 gap-2 sm:gap-4">
                          <div className="min-w-0">
                            <label className="text-xs sm:text-sm font-medium text-gray-600 block mb-1 sm:mb-2">Start Time</label>
                            <input
                              type="time"
                              value={range.start}
                              onChange={(e) => {
                                const updated = [...(dateTimeRanges[date] || [])];
                                updated[index] = { ...updated[index], start: e.target.value };
                                setDateTimeRanges((prev) => ({ ...prev, [date]: updated }));
                              }}
                              className="w-full max-w-full border border-gray-200 rounded-lg px-2 sm:px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 box-border"
                            />
                          </div>
                          <div className="min-w-0">
                            <label className="text-xs sm:text-sm font-medium text-gray-600 block mb-1 sm:mb-2">End Time</label>
                            <input
                              type="time"
                              value={range.end}
                              onChange={(e) => {
                                const updated = [...(dateTimeRanges[date] || [])];
                                updated[index] = { ...updated[index], end: e.target.value };
                                setDateTimeRanges((prev) => ({ ...prev, [date]: updated }));
                              }}
                              className="w-full max-w-full border border-gray-200 rounded-lg px-2 sm:px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 box-border"
                            />
                          </div>
                        </div>
                        <div className="mt-2 flex justify-end">
                          <button
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors duration-200 text-sm flex items-center"
                            onClick={() => {
                              const updated = [...(dateTimeRanges[date] || [])];
                              updated.splice(index, 1);
                              setDateTimeRanges((prev) => ({ ...prev, [date]: updated }));
                            }}
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Remove
                          </button>
                        </div>
                      </div>
                ))}

                <button
                  onClick={() => {
                    const updated = [...(dateTimeRanges[date] || [])];
                    updated.push({ start: '', end: '' });
                    setDateTimeRanges((prev) => ({ ...prev, [date]: updated }));
                  }}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors duration-200"
                >
                      <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Another Time Range
                </button>
              </div>
            ))}
              </div>

              <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                  className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200 border border-gray-200 hover:border-gray-300"
                onClick={() => setShowTimeModal(false)}
              >
                Cancel
              </button>
              <button
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
                onClick={() => {
                  // Validate 3-hour minimum per day before saving
                  for (const date of selectedDates) {
                    const timeRanges = Array.isArray(dateTimeRanges[date])
                      ? dateTimeRanges[date]
                      : dateTimeRanges[date]
                        ? [dateTimeRanges[date]]
                        : [];

                    for (let i = 0; i < timeRanges.length; i++) {
                      const { start, end } = timeRanges[i];
                      if (!start || !end) continue;
                      const [startH, startM] = start.split(':').map(Number);
                      const [endH, endM] = end.split(':').map(Number);
                      const startTotal = startH * 60 + startM;
                      const endTotal = endH * 60 + endM;
                      const dateLabel = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                        weekday: 'short', month: 'short', day: 'numeric'
                      });
                      if (endTotal <= startTotal) {
                        alert(`Start time must be before end time for range ${i + 1} on ${dateLabel}.\n\nFor overnight care, split into two date entries: end the first day at 11:59 PM and start the next day at 12:00 AM.`);
                        return;
                      }
                      const rangeMinutes = endTotal - startTotal;
                      if (rangeMinutes < 180) {
                        alert(`There is a 3-hour minimum required for all time ranges. Range ${i + 1} on ${dateLabel} is only ${Math.floor(rangeMinutes / 60)}h ${rangeMinutes % 60}m. Please adjust your times.`);
                        return;
                      }
                    }
                  }
                  setSchedule(dateTimeRanges);
                  setShowTimeModal(false);
                }}
              >
                  Save Schedule
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
  
};

export default PostJob;